import React from 'react'
import validation from './Send.validation'
import * as Joi from 'joi-browser'
import moment from 'moment'
import { Alert, Button, Form, Input } from 'reactstrap'
import DatePicker from 'react-datepicker'
import l10n from '../../localization'
import { connect } from 'react-redux'
import {
  meterErrorClear,
  newMeterReading,
} from '../../redux/actions/meter.actions'
import * as Styled from './Send.styles'

const RegularReader = ({
  selectedMeter,
  selectedUserMeter,
  meters = [],
  newMeterReading,
  meterErrorClear,
}) => {
  const initialMeterState = {
    readingDate: null,
    value: null,
  }

  const [validate, setValidation] = React.useState({
    ...initialMeterState,
    readingDate: '',
  })

  const [readingDate, setReadingDate] = React.useState(moment())
  const [readingValue, setReadingValue] = React.useState(null)
  const [readingValueAlertVisible, setReadingValueAlertVisible] =
    React.useState(false)

  let schema = Joi.object().keys({
    meterSiteCode: Joi.string().required(),
    meterCode: Joi.string().required(),
    readingDate: Joi.string().required(),
    value: Joi.number().required(),
  })

  React.useEffect(() => {
    setReadingValueAlertVisible(false)
    setReadingValue(0)
  }, [selectedMeter, selectedUserMeter])

  const filterDates = (date) => {
    const { startDate, endDate } = getRestrictionDates()
    const currentDate = moment(date)
    return currentDate.isBetween(startDate, endDate, 'hour', '[]')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    meterErrorClear()

    if (readingValue < selectedUserMeter.lastValue) {
      setReadingValueAlertVisible(true)
      return
    }

    setReadingValueAlertVisible(false)

    const requestData = {
      meterSiteCode: selectedMeter.meterSiteCode,
      meterCode: selectedUserMeter.meterCode,
      readingDate: readingDate ? readingDate.format() : null,
      value: readingValue,
    }

    const validateResponse = validation(requestData, schema)

    if (!validateResponse) {
      newMeterReading(requestData)
      setValidation({ ...initialMeterState })
      return
    }

    setValidation(validateResponse)
  }

  const getRestrictionDates = () => {
    const currentDate = moment()

    // Date restrictions from 21st to 28th by default
    let enabledFromDay = 21
    let enabledToDay = 28

    // In case of February, offset a bit
    if (currentDate.month() === 1) {
      enabledFromDay = 19
      enabledToDay = 26
    }

    if (process.env.REACT_APP_MEASUREMENT_ALWAYS_ENABLE === 'true') {
      enabledFromDay = moment().startOf('month').date()
      enabledToDay = moment().endOf('month').date()
    }

    return {
      startDate: moment(currentDate).set('date', enabledFromDay).startOf('day'),
      endDate: moment(currentDate).set('date', enabledToDay).endOf('day'),
    }
  }

  function renderSubmissionSection() {
    const lastReadingDate = moment(selectedUserMeter.lastReadingDate)
    const { startDate, endDate } = getRestrictionDates()

    if (
      selectedMeter &&
      !selectedMeter.isOnlyActivated &&
      !selectedMeter.combined
    ) {
      return <Alert color="warning">{l10n.send.isOnlyActivated}</Alert>
    }

    if (
      lastReadingDate.month() === moment().month() &&
      lastReadingDate.year() === moment().year()
    ) {
      return (
        <Alert color="warning" hidden={meters.newPositionSaved}>
          {l10n.send.alreadySubmittedManual}
          <br />
          {l10n.send.alreadySubmittedManual2}
        </Alert>
      )
    } else {
      if (moment().isBetween(startDate, endDate, 'hour', '[]')) {
        return (
          <>
            <Alert color="success" hidden={!meters.newPositionSaved}>
              {l10n.send.sendSuccess}
            </Alert>
            {!meters.newPositionSaved && (
              <Button color="primary" type="submit">
                {l10n.send.sendButton}
              </Button>
            )}
          </>
        )
      } else {
        return <Alert color="warning">{l10n.send.sendDateRestriction}</Alert>
      }
    }
  }

  function renderMeterReadingView() {
    const currentDate = moment()
    const { startDate, endDate } = getRestrictionDates()

    if (
      selectedMeter &&
      selectedMeter.isOnlyActivated === false &&
      selectedMeter.combined === false
    ) {
      return null
    }

    if (currentDate.isBetween(startDate, endDate, 'hour', '[]')) {
      return (
        <Styled.MeterReadingView>
          <Styled.MeterReadingRow>
            <Styled.MeterReadingKey>
              {l10n.send.dateOfPosition}
            </Styled.MeterReadingKey>
            <Styled.MeterReadingValue>
              <DatePicker
                dateFormat="dd.MM.yyyy"
                selected={readingDate.toDate()}
                disabled={
                  !selectedMeter.isOnlyActivated && !selectedMeter.combined
                }
                required
                onChange={(newDate) => setReadingDate(moment(newDate))}
                filterDate={filterDates}
                locale={l10n.general.locale}
              />
            </Styled.MeterReadingValue>
          </Styled.MeterReadingRow>
          <Styled.MeterReadingRow>
            <Styled.MeterReadingKey>{l10n.send.reading}</Styled.MeterReadingKey>
            <Styled.MeterReadingValue>
              <Input
                type="number"
                name="readingValue"
                id="readingValue"
                value={readingValue || ''}
                placeholder="unesite novo stanje vodomjera"
                disabled={
                  !selectedMeter.isOnlyActivated && !selectedMeter.combined
                }
                invalid={!!validate.value}
                required
                onChange={(e) => setReadingValue(e.target.value)}
              />
            </Styled.MeterReadingValue>
          </Styled.MeterReadingRow>
        </Styled.MeterReadingView>
      )
    }

    return null
  }

  return (
    <Styled.RegularReaderWrapper>
      {selectedUserMeter && (
        <Form onSubmit={handleSubmit}>
          {selectedMeter && selectedMeter.isOnlyActivated && (
            <Styled.Card>
              <Styled.MeterDataView>
                <Styled.MeterDataRow>
                  <Styled.MeterDataKey>
                    {l10n.send.dateOfLastPosition}
                  </Styled.MeterDataKey>
                  <Styled.MeterDataValue>
                    {selectedUserMeter.lastReadingDate
                      ? moment(selectedUserMeter.lastReadingDate).format(
                          'DD.MM.YYYY'
                        )
                      : '-'}
                  </Styled.MeterDataValue>
                </Styled.MeterDataRow>
                <Styled.MeterDataRow>
                  <Styled.MeterDataKey>
                    {l10n.send.lastPosition}
                  </Styled.MeterDataKey>
                  <Styled.MeterDataValue>
                    {selectedUserMeter.lastValue}
                  </Styled.MeterDataValue>
                </Styled.MeterDataRow>
              </Styled.MeterDataView>

              {renderMeterReadingView()}
            </Styled.Card>
          )}

          <Styled.SubmitButtonView>
            {renderSubmissionSection()}
          </Styled.SubmitButtonView>
        </Form>
      )}

      <Alert
        color="danger"
        hidden={!meters.error}
        style={{ marginTop: '20px' }}
      >
        {l10n.serverErrors[meters.error] || meters.error}
      </Alert>

      <Alert
        color="danger"
        hidden={!readingValueAlertVisible}
        style={{ marginTop: '20px' }}
      >
        <Styled.LastPositionError>
          {l10n.send.lastPositionError}{' '}
          <Styled.Link to="/help">{l10n.send.lastPositionError2}</Styled.Link>{' '}
          {l10n.send.lastPositionError3}
        </Styled.LastPositionError>
      </Alert>
    </Styled.RegularReaderWrapper>
  )
}

export default connect(({ meters }) => ({ meters }), {
  newMeterReading,
  meterErrorClear,
})(RegularReader)
